import {
    appendHighlightedText,
    createCacheKey,
    debounce,
    exerciseRequestSize,
    filterExercises,
    getFirstFoundAlias,
    getJson,
    hasFoundExercise,
    toggleLoading,
    track,
} from "./exercisePickerShared";

const itemsPerPage = 12;
const requestSize = exerciseRequestSize;

let countFormatter = null;
let countFormatterLang = null;

function formatCount(count) {
    const lang = document.documentElement.lang || undefined;

    try {
        if (!countFormatter || countFormatterLang !== lang) {
            countFormatter = new Intl.NumberFormat(lang);
            countFormatterLang = lang;
        }
        return countFormatter.format(count);
    } catch (e) {
        return String(count);
    }
}

function createExercisePicture(exercise) {
    const picture = document.createElement("picture");
    picture.className = "media-left image is-64x64";

    if (exercise.icon_url) {
        const source = document.createElement("source");
        source.type = "image/webp";
        source.srcset = exercise.icon_url.replace("128.png", "128.webp") + " 2x, " + exercise.icon_url.replace("128.png", "64.webp") + " 1x";
        picture.appendChild(source);

        const image = document.createElement("img");
        image.loading = "lazy";
        image.src = exercise.icon_url.replace("128.png", "64.png");
        image.srcset = exercise.icon_url.replace("128.png", "128.png") + " 2x, " + exercise.icon_url.replace("128.png", "64.png") + " 1x";
        image.alt = exercise.name || "";
        image.width = 64;
        image.height = 64;
        picture.appendChild(image);
    }

    return picture;
}

export function createExerciseSelector(options) {
    const root = typeof options.root === "string" ? document.querySelector(options.root) : options.root;
    if (!root) {
        return null;
    }

    const initialExercises = options.initialExercises || null;
    const searchInput = root.querySelector("[data-exercise-selector-search]");
    const searchControl = root.querySelector("[data-exercise-selector-search-control]");
    const bodypartSelect = root.querySelector("[data-exercise-selector-bodypart]");
    const categorySelect = root.querySelector("[data-exercise-selector-category]");
    const listNode = root.querySelector("[data-exercise-selector-list]");
    const statusNode = root.querySelector("[data-exercise-selector-status]");
    const statusTextNode = root.querySelector("[data-exercise-selector-status-text]");
    const moreButton = root.querySelector("[data-exercise-selector-more]");
    const baseUrl = options.baseUrl || root.dataset.baseUrl || "";
    const loadingText = root.dataset.loadingText || "Loading exercises...";
    const emptyText = root.dataset.emptyText || "Sorry - we can't find that exercise.";
    const alsoKnownAsText = root.dataset.alsoKnownAs || "Also known as:";
    const liftsText = root.dataset.liftsText || "lifts";
    const responseCache = {};

    const state = {
        loadingKey: null,
        searching: false,
        count: initialExercises && initialExercises.meta ? initialExercises.meta.count : Number.POSITIVE_INFINITY,
        bodypart: bodypartSelect ? bodypartSelect.value : "",
        category: categorySelect ? categorySelect.value : "",
        searchText: searchInput ? searchInput.value : "",
        exercises: initialExercises && initialExercises.data ? initialExercises.data : [],
        visibleExercises: [],
        filteredCount: 0,
        hasMore: false,
        limit: itemsPerPage,
    };

    function getLinkHref(exercise) {
        return baseUrl.replace(/\/$/, "") + "/" + exercise.name_url;
    }

    function createParams() {
        const limit = Math.ceil(state.limit / requestSize) * requestSize;
        const params = {
            limit: limit,
            "exercise.fields": "category,name_url,bodypart,count,aliases,icon_url",
            standard: "true",
        };

        if (state.searchText !== "") {
            params.query = state.searchText;
        }
        if (state.category !== "") {
            params.category = state.category;
        }
        if (state.bodypart !== "") {
            params.bodypart = state.bodypart;
        }
        return params;
    }

    function getFilteredExerciseList() {
        return filterExercises(state.exercises, state.category, state.bodypart, state.searchText);
    }

    function updateVisibleExercises(filteredExercises) {
        const exercises = filteredExercises.slice(0, state.limit);
        const hasMore = state.count > state.limit || filteredExercises.length > state.limit;

        state.visibleExercises = exercises;
        state.filteredCount = filteredExercises.length;
        state.hasMore = hasMore;

        return {
            exercises: exercises,
            hasMore: hasMore,
        };
    }

    function selectExercise(exercise) {
        window.location = getLinkHref(exercise);
    }

    function createExerciseNode(exercise) {
        const column = document.createElement("div");
        column.className = "column is-half-mobile is-one-third-tablet is-one-quarter-desktop exerciseitem";

        const link = document.createElement("a");
        link.href = getLinkHref(exercise);
        link.className = "button is-fullwidth exerciseitem__button";

        const article = document.createElement("article");
        article.className = "media";
        article.appendChild(createExercisePicture(exercise));

        const mediaContent = document.createElement("div");
        mediaContent.className = "media-content";

        const title = document.createElement("span");
        title.className = "title is-size-6 is-size-7-mobile mb-1";
        appendHighlightedText(title, exercise.name, state.searchText);
        mediaContent.appendChild(title);

        if (exercise.count > 0) {
            const count = document.createElement("span");
            count.className = "is-size-7";
            count.textContent = formatCount(exercise.count) + " " + liftsText;
            mediaContent.appendChild(count);
        }

        article.appendChild(mediaContent);
        link.appendChild(article);

        const foundAlias = getFirstFoundAlias(exercise, state.searchText);
        if (state.searchText !== "" && !hasFoundExercise(exercise, state.searchText) && foundAlias) {
            const alias = document.createElement("div");
            alias.className = "is-size-7 pt-3 also-known-as";
            alias.appendChild(document.createTextNode(alsoKnownAsText + " "));
            appendHighlightedText(alias, foundAlias, state.searchText);
            link.appendChild(alias);
        }

        column.appendChild(link);
        return column;
    }

    function renderControls(exercises, hasMore) {
        exercises = exercises || state.visibleExercises;
        hasMore = typeof hasMore === "boolean" ? hasMore : state.hasMore;

        const isBusy = !!state.loadingKey || state.searching;
        toggleLoading(searchControl, isBusy);
        if (moreButton) {
            moreButton.classList.toggle("is-loading", isBusy);
            moreButton.disabled = isBusy || !hasMore;
            moreButton.setAttribute("aria-disabled", isBusy || !hasMore ? "true" : "false");
        }
        if (statusNode) {
            statusNode.classList.toggle("is-hidden", exercises.length > 0);
        }
        if (statusTextNode) {
            statusTextNode.textContent = isBusy ? loadingText : emptyText;
        }
    }

    function renderList(exercises, hasMore) {
        if (!listNode) {
            renderControls(exercises, hasMore);
            return;
        }

        listNode.innerHTML = "";
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < exercises.length; i++) {
            fragment.appendChild(createExerciseNode(exercises[i]));
        }
        listNode.appendChild(fragment);
        renderControls(exercises, hasMore);
    }

    function renderFiltered(filteredExercises) {
        const visible = updateVisibleExercises(filteredExercises);
        renderList(visible.exercises, visible.hasMore);
    }

    function render() {
        renderFiltered(getFilteredExerciseList());
    }

    function loadExercises() {
        const params = createParams();
        const loadingKey = createCacheKey("exercises", params);

        if (responseCache[loadingKey]) {
            state.loadingKey = null;
            state.searching = false;
            state.exercises = responseCache[loadingKey].data || [];
            state.count = responseCache[loadingKey].meta ? responseCache[loadingKey].meta.count : 0;
            render();
            return Promise.resolve();
        }

        state.loadingKey = loadingKey;
        renderControls();

        return getJson("exercises", params).then(function (response) {
            responseCache[loadingKey] = response;
            if (state.loadingKey === loadingKey) {
                state.exercises = response.data || [];
                state.count = response.meta ? response.meta.count : 0;
            }
        }).catch(function () {
            if (state.loadingKey === loadingKey) {
                state.count = 0;
            }
        }).then(function () {
            if (state.loadingKey === loadingKey) {
                state.loadingKey = null;
                state.searching = false;
                render();
            } else {
                renderControls();
            }
        });
    }

    function loadExercisesIfRequired(isSubset) {
        const filteredExercises = getFilteredExerciseList();
        if (!isSubset || state.limit > filteredExercises.length) {
            return loadExercises();
        }
        renderFiltered(filteredExercises);
        return Promise.resolve();
    }

    const loadSearchResults = debounce(function () {
        loadExercises();
    }, 200);

    function updateSearch(value) {
        const oldValue = state.searchText;
        const isSubset = oldValue === "" || value.indexOf(oldValue) === 0;
        state.searchText = value;
        state.limit = itemsPerPage;

        const filteredExercises = getFilteredExerciseList();
        renderFiltered(filteredExercises);

        if (!isSubset || state.limit > filteredExercises.length) {
            state.searching = true;
            renderControls();
            loadSearchResults();
        } else {
            state.searching = false;
            loadSearchResults.cancel();
            renderControls();
        }
    }

    function initialise() {
        const filteredExercises = getFilteredExerciseList();
        updateVisibleExercises(filteredExercises);
        renderControls(state.visibleExercises, state.hasMore);

        if (!initialExercises) {
            loadExercises();
        }
    }

    if (searchInput) {
        searchInput.addEventListener("input", function (event) {
            updateSearch(event.target.value);
        });
        searchInput.addEventListener("keydown", function (event) {
            if (event.key === "Enter" || event.keyCode === 13) {
                event.preventDefault();
                event.stopPropagation();
                const firstExercise = state.visibleExercises[0];
                if (firstExercise) {
                    selectExercise(firstExercise);
                }
            }
        });
    }

    if (categorySelect) {
        categorySelect.addEventListener("change", function (event) {
            const oldValue = state.category;
            state.category = event.target.value;
            state.limit = itemsPerPage;
            loadExercisesIfRequired(oldValue === "");
            if (state.category) {
                track("filter-group", "exercise-selector", state.category);
            }
        });
    }

    if (bodypartSelect) {
        bodypartSelect.addEventListener("change", function (event) {
            const oldValue = state.bodypart;
            state.bodypart = event.target.value;
            state.limit = itemsPerPage;
            loadExercisesIfRequired(oldValue === "");
            if (state.bodypart) {
                track("filter-body-part", "exercise-selector", state.bodypart);
            }
        });
    }

    if (moreButton) {
        moreButton.addEventListener("click", function (event) {
            if (state.loadingKey || state.searching || !state.hasMore) {
                event.preventDefault();
                return;
            }
            state.limit += itemsPerPage;
            loadExercisesIfRequired(true);
        });
    }

    initialise();

    return {
        render: render,
    };
}